import { find, isObject, every } from 'lodash'
import { Roles } from 'utils/user'
import Decorator from './decorator'
import moment from 'moment'

export default class UserDecorator extends Decorator {
  /**
   * Determine if user exists.
   *
   * @return boolean
   */
  exists () {
    return isObject(this.decorated)
  }

  /**
   * Determine if user is an admin.
   *
   * @return boolean
   */
  isAdmin () {
    return this.is(Roles.ADMIN)
  }

  /**
   * Determine if user is a system admin. This is a child account user that has
   * system administrator permissions granted.
   *
   * Also returns true for regular administrator users as they are technically
   * system admins.
   *
   * @return boolean
   */
  isSystemAdmin () {
    if (this.isChild && this.isAdmin) {
      return this.decorated.permissions.administrator === true
    }

    return this.isAdmin
  }

  /**
   * Determine if user is NOT an admin.
   *
   * @return boolean
   */
  isNotAdmin () {
    return !this.is(Roles.ADMIN)
  }

  /**
   * Determine if user is an admin or a coordinator.
   *
   * @return boolean
   */
  isAdminOrCoordinator () {
    return this.is(Roles.ADMIN, Roles.COORDINATOR)
  }

  /**
   * Determine if user is a client.
   *
   * @return boolean
   */
  isClient () {
    return this.is(Roles.CLIENT)
  }

  /**
   * Determine if user is a carrier.
   *
   * @return boolean
   */
  isCarrier () {
    return this.is(Roles.CARRIER)
  }

  /**
   * Determine if user is a coordinator.
   *
   * @return boolean
   */
  isCoordinator () {
    return this.is(Roles.COORDINATOR)
  }

  /**
   * Determine if user is a driver.
   *
   * @return boolean
   */
  isDriver () {
    return this.is(Roles.DRIVER)
  }

  /**
   * Determine if user is a client or carrier.
   *
   * @return boolean
   */
  isClientOrCarrier () {
    return this.is(Roles.CARRIER, Roles.CLIENT)
  }

  /**
   * Determine if user is a carrier or driver
   *
   * @returns {boolean}
   */
  isCarrierOrDriver () {
    return this.is(Roles.CARRIER, Roles.DRIVER)
  }

  /**
   * Determine if user is a one or more given roles.
   *
   * Will return true if user has one of the given roles, it's not an exact match.
   *
   * @return boolean
   */
  is (roles) {
    return find(arguments, role => role === this.decorated.role_id) !== undefined
  }

  /**
   * Determine if a user can manage job requests.
   *
   * @return boolean
   */
  canManageJobRequests () {
    return this.isSystemAdmin || this.isCoordinator
  }

  /**
   * Determine if a user can manage jobs.
   *
   * @return boolean
   */
  canManageJobs () {
    return this.isSystemAdmin || this.isCoordinator || this.isClientOrCarrier
  }

  /**
   * Determine if a user can access the job board.
   *
   * @return boolean
   */
  canManageJobBoard () {
    return this.isSystemAdmin || this.isCoordinator || this.isCarrier
  }

  /**
   * Determine if a user can manage locations.
   *
   * @return boolean
   */
  canManageLocations () {
    return this.isSystemAdmin || this.isCoordinator || this.isClientOrCarrier
  }

  /**
   * Determine if a user can manage trucks.
   *
   * @return boolean
   */
  canManageTrucks () {
    return this.isSystemAdmin || this.isCoordinator || this.isCarrier
  }

  /**
   * Determine if a user can manage drivers.
   *
   * @return boolean
   */
  canManageDrivers () {
    return this.isSystemAdmin || this.isCoordinator || this.isCarrier
  }

  /**
   * Determine if a user can manage loads.
   *
   * @return boolean
   */
  canManageLoads () {
    return this.isSystemAdmin || this.isCoordinator || this.isCarrier
  }

  /**
   * Determine if a user can manage load checks.
   *
   * @return boolean
   */
  canManageLoadChecks () {
    return this.permissionTo('load_check')
  }

  /**
   * Determine if a user can manage invoices.
   *
   * @return boolean
   */
  canManageInvoices () {
    return this.isClientOrCarrier || this.permissionTo('invoices')
  }

  /**
   * Determine if a user can manage payments.
   *
   * @return boolean
   */
  canManagePayments () {
    return this.isCarrier || this.permissionTo('payments')
  }

  /**
   * Determine if a user can manage reports.
   *
   * @return boolean
   */
  canManageReports () {
    return this.isCarrier || this.permissionTo('reports')
  }

  /**
   * Determine if a user can manage users.
   *
   * @return boolean
   */
  canManageUsers () {
    return this.permissionTo('users')
  }

  /**
   * Determine if a user can manage system settings.
   *
   * @return boolean
   */
  canManageSettings () {
    return this.permissionTo('settings')
  }

  /**
   * Determine if a user can see overview map.
   *
   * @return boolean
   */
  canSeeMap () {
    return this.isSystemAdmin || this.isCoordinator || this.isClientOrCarrier
  }

  /**
   * Determine if a user can see notifications
   * @returns {UserDecorator.isSystemAdmin|(function(): boolean)}
   */
  canSeeNotifications () {
    return this.isSystemAdmin || this.isCoordinator
  }

  /**
   * Determine if a user has permission to perform an action.
   *
   * @param mixed|array permissions
   *
   * @return boolean
   */
  permissionTo (permissions) {
    const noCoordinatorsAllowed = []

    // If the user is a master user we'll check to see if they are a coordinator, if so we'll
    // reject them if they are trying to do something coordinators are not allowed to do.
    if (this.isMaster) {
      if (this.isCoordinator) {
        return find(arguments, permission => noCoordinatorsAllowed.indexOf(permission) !== -1) === undefined
      } else {
        return this.isAdmin
      }
    }

    // If they are not a master user then we will check if they have the global "administrator"
    // permission. If so they can do anything a normal admin can do.
    if (this.isSystemAdmin) {
      return true
    }

    if (arguments.length === 0) {
      return false
    }

    return every(arguments, permission => this.decorated.permissions[permission] === true)
  }

  /**
   * Determine if user has not been activated. Carriers that are not yet verified
   * are deemed not activated.
   *
   * @return boolean
   */
  isNotActivated () {
    return this.isCarrier && this.decorated.carrier_verified === false
  }

  /**
   * Determine if user is activated. If they are a client or they are a carrier
   * and they have been verified as one they are activated.
   *
   * @return boolean
   */
  isActivated () {
    return this.isClient || !this.isNotActivated
  }

  /**
   * Determine if user is enabled.
   *
   * @return boolean
   */
  isEnabled () {
    return this.decorated.active === true
  }

  /**
   * Determine if user is disabled.
   *
   * @return boolean
   */
  isDisabled () {
    return this.decorated.active === false
  }

  /**
   * Determine if user is archived.
   *
   * @return boolean
   */
  isArchived () {
    return this.decorated.deleted_at !== null
  }

  /**
   * Determine if user is not archived.
   *
   * @return boolean
   */
  isNotArchived () {
    return this.decorated.deleted_at === null
  }

  /**
   * Determine if user is unverified.
   *
   * @return boolean
   */
  isUnverified () {
    return this.decorated.email_verified === false
  }

  /**
   * Determine if user is a child account.
   *
   * @return boolean
   */
  isChild () {
    return this.decorated.parent_id !== null
  }

  /**
   * Determine if user is a master account.
   *
   * @return boolean
   */
  isMaster () {
    return this.decorated.parent_id === null
  }

  /**
   * Users private Pusher channel.
   *
   * @return string
   */
  privateChannel () {
    return `user.${this.decorated.id}`
  }

  /**
   * Users last login date/time.
   *
   * @return string
   */
  lastLogin () {
    if (this.decorated.last_login) {
      return moment(this.decorated.last_login).format('DD/MM/YYYY HH:mm:ss')
    }

    return '–'
  }

  /**
   * Get the URL to the drivers thumbnail.
   *
   * @return string
   */
  thumbnailUrl () {
    if (this.decorated.driver_profile) {
      if (this.decorated.driver_profile.photo_storage_path) {
        return `${this.decorated.driver_profile.photo_storage_path}/256x256.jpg?${moment().unix()}`
      }
    }

    return null
  }
}



// WEBPACK FOOTER //
// ./src/decorators/user.js